import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CANCELLATION_POLICIES, PROPERTY_TYPES, ROOM_TYPES } from "@sakani/contracts";
import { api, ApiError, mediaUrl, shrinkImage, uploadImage, type ListingFull, type Vocab } from "../api.ts";
import { day, POLICY } from "../format.ts";
import { Act } from "../ui.tsx";
import { LISTING_STATUS } from "./Catalogue.tsx";

const PROPERTY: Record<string, string> = { apartment: "Appartement", house: "Maison", villa: "Villa", riad: "Riad", guesthouse: "Maison d'hôtes", hotel_room: "Chambre d'hôtel", desert_camp: "Camp désertique", kasbah: "Kasbah", other: "Autre" };
const ROOM: Record<string, string> = { entire_place: "Logement entier", private_room: "Chambre privée", shared_room: "Chambre partagée" };

interface Form {
  cityId: number; titleFr: string; titleAr: string; descFr: string; descAr: string; propertyType: string; roomType: string; address: string; lat: string; lng: string;
  maxGuests: number; bedrooms: number; beds: number; bathrooms: number; price: string; cleaning: string; checkInTime: string; checkOutTime: string;
  minNights: number; maxNights: number; cancellationPolicy: string; rules: string; amenityIds: number[];
}
/** Coordinates are optional (a map is coming later) but must be given together and be real numbers. */
const coordsOk = (f: Pick<Form, "lat" | "lng">) => {
  const a = f.lat.trim(), b = f.lng.trim();
  if (a === "" && b === "") return true;
  if (a === "" || b === "") return false;
  return Math.abs(Number(a)) <= 90 && Math.abs(Number(b)) <= 180 && !Number.isNaN(Number(a)) && !Number.isNaN(Number(b));
};
const EMPTY: Form = { cityId: 0, titleFr: "", titleAr: "", descFr: "", descAr: "", propertyType: "apartment", roomType: "entire_place", address: "", lat: "", lng: "", maxGuests: 2, bedrooms: 1, beds: 1, bathrooms: 1, price: "", cleaning: "0", checkInTime: "15:00", checkOutTime: "11:00", minNights: 1, maxNights: 30, cancellationPolicy: "moderate", rules: "", amenityIds: [] };
const toMinor = (mad: string) => Math.round(Number(mad.replace(",", ".")) * 100);
const fromListing = (l: ListingFull): Form => ({
  cityId: l.cityId, titleFr: l.title.fr, titleAr: l.title.ar ?? "", descFr: l.description.fr, descAr: l.description.ar ?? "", propertyType: l.propertyType, roomType: l.roomType, address: l.address,
  lat: l.lat == null ? "" : String(l.lat), lng: l.lng == null ? "" : String(l.lng), maxGuests: l.maxGuests, bedrooms: l.bedrooms, beds: l.beds, bathrooms: Number(l.bathrooms), price: String(l.basePriceMinor / 100), cleaning: String(l.cleaningFeeMinor / 100),
  checkInTime: l.checkInTime, checkOutTime: l.checkOutTime, minNights: l.minNights, maxNights: l.maxNights, cancellationPolicy: l.cancellationPolicy, rules: l.houseRules.join("\n"), amenityIds: l.amenities.map((a) => a.id),
});
const toBody = (f: Form) => ({
  cityId: f.cityId, title: { fr: f.titleFr.trim(), ...(f.titleAr.trim() ? { ar: f.titleAr.trim() } : {}) }, description: { fr: f.descFr.trim(), ...(f.descAr.trim() ? { ar: f.descAr.trim() } : {}) },
  propertyType: f.propertyType, roomType: f.roomType, address: f.address.trim(), lat: f.lat.trim() === "" ? null : Number(f.lat), lng: f.lng.trim() === "" ? null : Number(f.lng), maxGuests: f.maxGuests, bedrooms: f.bedrooms, beds: f.beds, bathrooms: f.bathrooms,
  basePriceMinor: toMinor(f.price), cleaningFeeMinor: toMinor(f.cleaning || "0"), checkInTime: f.checkInTime, checkOutTime: f.checkOutTime, minNights: f.minNights, maxNights: f.maxNights,
  cancellationPolicy: f.cancellationPolicy, houseRules: f.rules.split("\n").map((s) => s.trim()).filter(Boolean), amenityIds: f.amenityIds, categoryIds: [] as number[],
});

export function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [vocab, setVocab] = useState<Vocab | null>(null);
  const [listing, setListing] = useState<ListingFull | null>(null);
  const [f, setF] = useState<Form>(EMPTY);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => { setF((p) => ({ ...p, [k]: v })); setSaved(false); };

  const load = useCallback(async () => {
    if (!id) return;
    const l = await api<ListingFull>(`/v1/host/listings/${id}`);
    setListing(l); setF(fromListing(l));
  }, [id]);
  useEffect(() => { api<Vocab>("/v1/vocab").then((v) => { setVocab(v); setF((p) => (p.cityId ? p : { ...p, cityId: v.cities[0]?.id ?? 0 })); }); }, []);
  useEffect(() => { void load(); }, [load]);

  const valid = f.titleFr.trim().length > 1 && f.descFr.trim().length > 1 && f.address.trim().length > 2 && f.price !== "" && coordsOk(f) && f.cityId > 0;
  const grouped = useMemo(() => {
    const m = new Map<string, Vocab["amenities"]>();
    for (const a of vocab?.amenities ?? []) m.set(a.category, [...(m.get(a.category) ?? []), a]);
    return [...m.entries()];
  }, [vocab]);

  async function save() {
    setErr(null);
    try {
      if (!id) {
        const row = await api<{ id: string }>("/v1/staff/house/listings", { body: toBody(f) });
        nav(`/catalogue/${row.id}`, { replace: true });
      } else {
        const l = await api<ListingFull>(`/v1/host/listings/${id}`, { method: "PATCH", body: toBody(f) });
        setListing((p) => (p ? { ...p, ...l } : p)); setSaved(true);
      }
    } catch (e) { setErr(e instanceof ApiError ? e.message : "Échec de l'enregistrement"); throw e; }
  }

  const locked = listing && listing.ownerType === "host";
  return (
    <section>
      <Link to="/catalogue" className="back small muted">← Catalogue</Link>
      <header className="pagehead">
        <h1>{id ? listing?.title.fr ?? "…" : "Nouveau bien Sekani"}</h1>
        {listing && <p className="muted small"><span className="pill sm">{LISTING_STATUS[listing.status] ?? listing.status}</span> {listing.ownerType === "platform" ? "Bien Sekani — nous confirmons les séjours." : "Bien partenaire."}</p>}
      </header>

      <div className="grid2">
        <form className="stackc" onSubmit={(e) => { e.preventDefault(); void save().catch(() => undefined); }}>
          <div className="card"><b>Présentation</b>
            <label className="field"><span>Ville</span><select value={f.cityId} onChange={(e) => set("cityId", Number(e.target.value))}>{vocab?.cities.map((c) => <option key={c.id} value={c.id}>{c.name.fr}</option>)}</select></label>
            <label className="field"><span>Titre (français)</span><input value={f.titleFr} onChange={(e) => set("titleFr", e.target.value)} maxLength={120} /></label>
            <label className="field"><span>Titre (arabe)</span><input dir="rtl" value={f.titleAr} onChange={(e) => set("titleAr", e.target.value)} maxLength={120} /></label>
            <label className="field"><span>Description (français)</span><textarea rows={5} value={f.descFr} onChange={(e) => set("descFr", e.target.value)} /></label>
            <label className="field"><span>Description (arabe)</span><textarea dir="rtl" rows={5} value={f.descAr} onChange={(e) => set("descAr", e.target.value)} /></label>
          </div>
          <div className="card"><b>Le bien</b>
            <div className="two">
              <label className="field"><span>Type</span><select value={f.propertyType} onChange={(e) => set("propertyType", e.target.value)}>{PROPERTY_TYPES.map((p) => <option key={p} value={p}>{PROPERTY[p]}</option>)}</select></label>
              <label className="field"><span>Location</span><select value={f.roomType} onChange={(e) => set("roomType", e.target.value)}>{ROOM_TYPES.map((p) => <option key={p} value={p}>{ROOM[p]}</option>)}</select></label>
            </div>
            <label className="field"><span>Adresse (jamais publiée — pour nos équipes)</span><input value={f.address} onChange={(e) => set("address", e.target.value)} /></label>
            <div className="two">
              <label className="field"><span>Latitude (facultatif)</span><input inputMode="decimal" value={f.lat} onChange={(e) => set("lat", e.target.value)} /></label>
              <label className="field"><span>Longitude (facultatif)</span><input inputMode="decimal" value={f.lng} onChange={(e) => set("lng", e.target.value)} /></label>
            </div>
            <div className="two">
              <Num label="Voyageurs max" v={f.maxGuests} on={(n) => set("maxGuests", n)} min={1} />
              <Num label="Chambres" v={f.bedrooms} on={(n) => set("bedrooms", n)} min={0} />
              <Num label="Lits" v={f.beds} on={(n) => set("beds", n)} min={1} />
              <Num label="Salles de bain" v={f.bathrooms} on={(n) => set("bathrooms", n)} min={0.5} step={0.5} />
            </div>
          </div>
          <div className="card"><b>Tarifs et règles</b>
            <div className="two">
              <label className="field"><span>Prix par nuit (MAD)</span><input inputMode="decimal" value={f.price} onChange={(e) => set("price", e.target.value)} /></label>
              <label className="field"><span>Frais de ménage (MAD)</span><input inputMode="decimal" value={f.cleaning} onChange={(e) => set("cleaning", e.target.value)} /></label>
              <label className="field"><span>Arrivée</span><input type="time" value={f.checkInTime} onChange={(e) => set("checkInTime", e.target.value)} /></label>
              <label className="field"><span>Départ</span><input type="time" value={f.checkOutTime} onChange={(e) => set("checkOutTime", e.target.value)} /></label>
              <Num label="Nuits min." v={f.minNights} on={(n) => set("minNights", n)} min={1} />
              <Num label="Nuits max." v={f.maxNights} on={(n) => set("maxNights", n)} min={1} />
            </div>
            <label className="field"><span>Annulation</span><select value={f.cancellationPolicy} onChange={(e) => set("cancellationPolicy", e.target.value)}>{CANCELLATION_POLICIES.map((p) => <option key={p} value={p}>{POLICY[p]}</option>)}</select></label>
            <label className="field"><span>Règles de la maison (une par ligne)</span><textarea rows={3} value={f.rules} onChange={(e) => set("rules", e.target.value)} /></label>
          </div>
          <div className="card"><b>Équipements</b>
            {grouped.map(([cat, list]) => (
              <fieldset key={cat} className="amen"><legend className="eyebrow">{cat}</legend>
                {list.map((a) => (
                  <label key={a.id} className="check"><input type="checkbox" checked={f.amenityIds.includes(a.id)} onChange={(e) => set("amenityIds", e.target.checked ? [...f.amenityIds, a.id] : f.amenityIds.filter((x) => x !== a.id))} /> {a.key.replaceAll("_", " ")}</label>
                ))}
              </fieldset>
            ))}
          </div>
          {err && <p className="err" role="alert">{err}</p>}
          <div className="quick"><button className="btn" type="submit" disabled={!valid}>{id ? "Enregistrer" : "Créer le brouillon"}</button>{saved && <span className="muted small">Enregistré.</span>}{!id && <span className="muted small">Photos et tarifs saisonniers après la création.</span>}</div>
        </form>

        {listing && id && <Side listing={listing} reload={load} nav={() => nav("/catalogue")} locked={!!locked} />}
      </div>
    </section>
  );
}

function Num({ label, v, on, min, step = 1 }: { label: string; v: number; on: (n: number) => void; min: number; step?: number }) {
  return <label className="field"><span>{label}</span><input type="number" inputMode="decimal" min={min} step={step} value={v} onChange={(e) => on(Number(e.target.value))} /></label>;
}

function Side({ listing, reload, nav }: { listing: ListingFull; reload: () => Promise<void>; nav: () => void; locked: boolean }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const photos = [...listing.photos].sort((a, b) => a.position - b.position);

  async function add(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true); setErr(null);
    try {
      for (const file of Array.from(files)) {
        const key = await uploadImage("listing", await shrinkImage(file));
        await api(`/v1/host/listings/${listing.id}/photos`, { body: { key } });
      }
      await reload();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  const videos = [...(listing.videos ?? [])].sort((a, b) => a.position - b.position);
  async function addVideos(files: FileList | null) {
    if (!files?.length) return;
    setBusy(true); setErr(null);
    try {
      for (const file of Array.from(files)) {
        const key = await uploadImage("video", file);
        await api(`/v1/host/listings/${listing.id}/videos`, { body: { key, sizeBytes: file.size } });
      }
      await reload();
    } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  const move = async (i: number, d: -1 | 1) => {
    const ids = photos.map((p) => p.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    await api(`/v1/host/listings/${listing.id}/photos/order`, { method: "PUT", body: { ids } });
    await reload();
  };

  return (
    <aside className="stackc">
      <div className="card"><b>Photos ({photos.length})</b>
        <p className="muted small">La première est la photo de couverture. Il en faut au moins 5 pour publier.</p>
        <ul className="photos">
          {photos.map((p, i) => (
            <li key={p.id}>
              <img src={mediaUrl(p.key)} alt="" loading="lazy" />
              <span className="ph-actions">
                <button type="button" className="chip" onClick={() => void move(i, -1)} disabled={i === 0} aria-label="Avancer">←</button>
                <button type="button" className="chip" onClick={() => void move(i, 1)} disabled={i === photos.length - 1} aria-label="Reculer">→</button>
                <button type="button" className="chip" onClick={async () => { await api(`/v1/host/listings/${listing.id}/photos/${p.id}`, { method: "DELETE" }); await reload(); }} aria-label="Supprimer">✕</button>
              </span>
            </li>
          ))}
        </ul>
        <label className="btn ghost filebtn">{busy ? "Envoi…" : "Ajouter des photos"}<input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden disabled={busy} onChange={(e) => { void add(e.target.files); e.target.value = ""; }} /></label>
        {err && <p className="err" role="alert">{err}</p>}
      </div>

      <div className="card"><b>Vidéos ({videos.length})</b>
        <p className="muted small">Facultatif — MP4, WebM ou MOV, 100 Mo max. Affichées après les photos.</p>
        <ul className="photos">
          {videos.map((v) => (
            <li key={v.id}>
              <video src={mediaUrl(v.key)} preload="metadata" muted />
              <span className="ph-actions">
                <button type="button" className="chip" onClick={async () => { await api(`/v1/host/listings/${listing.id}/videos/${v.id}`, { method: "DELETE" }); await reload(); }} aria-label="Supprimer">✕</button>
              </span>
            </li>
          ))}
        </ul>
        <label className="btn ghost filebtn">{busy ? "Envoi…" : "Ajouter une vidéo"}<input type="file" accept="video/mp4,video/webm,video/quicktime" hidden disabled={busy} onChange={(e) => { void addVideos(e.target.files); e.target.value = ""; }} /></label>
      </div>

      <Seasons listing={listing} />

      <div className="actions">
        <b>Publication</b>
        {listing.status === "draft" && <><p className="muted small">Envoyez en validation, puis publiez. Pour un bien Sekani, c'est immédiat.</p>
          <Act run={async () => { await api(`/v1/host/listings/${listing.id}/submit`, { body: {} }); await reload(); }}>Envoyer en validation</Act></>}
        {listing.status === "pending_review" && <Act run={async () => { await api(`/v1/staff/listings/${listing.id}/approve`, { body: {} }); await reload(); }}>Publier</Act>}
        {listing.status === "published" && <><p className="muted small">En ligne, réservable.</p>
          <Act tone="ghost" run={async () => { await api(`/v1/host/listings/${listing.id}/unlist`, { body: {} }); await reload(); }}>Mettre hors ligne</Act></>}
        {listing.status !== "suspended" && listing.status !== "draft" && <Suspend id={listing.id} done={reload} />}
        {listing.status === "suspended" && <p className="muted small">Suspendu — contactez l'administrateur pour le rétablir.</p>}
        <button className="link small muted" type="button" onClick={nav}>Retour au catalogue</button>
      </div>
    </aside>
  );
}

function Suspend({ id, done }: { id: string; done: () => Promise<void> }) {
  const [reason, setReason] = useState("");
  return (
    <details className="cancel"><summary>Suspendre</summary>
      <input placeholder="Motif" value={reason} onChange={(e) => setReason(e.target.value)} />
      <Act tone="danger" disabled={reason.trim().length < 3} run={async () => { await api(`/v1/staff/listings/${id}/suspend`, { body: { reason: reason.trim() } }); await done(); }}>Suspendre ce bien</Act>
    </details>
  );
}

interface Rule { id: string; startDate: string; endDate: string; nightlyPriceMinor: number }
function Seasons({ listing }: { listing: ListingFull }) {
  const [rules, setRules] = useState<Rule[] | null>(null);
  const [a, setA] = useState(""); const [b, setB] = useState(""); const [p, setP] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    setRules(await api<Rule[]>(`/v1/host/listings/${listing.id}/price-rules`).catch(() => []));
  }, [listing.id]);
  useEffect(() => { void load(); }, [load]);
  return (
    <div className="card"><b>Tarifs saisonniers</b>
      <p className="muted small">Un prix par nuit différent sur une période (été, fêtes…).</p>
      {rules?.map((r) => (
        <div key={r.id} className="kv"><span className="num">{day(r.startDate)} → {day(r.endDate)} · {(r.nightlyPriceMinor / 100).toLocaleString("fr-FR")} {listing.currency}</span>
          <button className="link small" type="button" onClick={async () => { await api(`/v1/host/listings/${listing.id}/price-rules/${r.id}`, { method: "DELETE" }); await load(); }}>Retirer</button></div>
      ))}
      <div className="two"><label className="field"><span>Du</span><input type="date" value={a} onChange={(e) => setA(e.target.value)} /></label><label className="field"><span>Au (départ)</span><input type="date" value={b} onChange={(e) => setB(e.target.value)} /></label></div>
      <label className="field"><span>Prix par nuit ({listing.currency})</span><input inputMode="decimal" value={p} onChange={(e) => setP(e.target.value)} /></label>
      {err && <p className="err" role="alert">{err}</p>}
      <button className="btn ghost" type="button" disabled={!a || !b || !p} onClick={async () => {
        setErr(null);
        try { await api(`/v1/host/listings/${listing.id}/price-rules`, { body: { startDate: a, endDate: b, nightlyPriceMinor: toMinor(p) } }); setA(""); setB(""); setP(""); await load(); } catch (e) { setErr((e as Error).message); }
      }}>Ajouter la période</button>
    </div>
  );
}
